import { navigate } from 'gatsby'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { useProductPageContext } from 'src/components/restructure/product/contexts'
import { Statsig } from 'src/sdk/statsig'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { FEM_MASC_TENIS_PDP_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'

import type { GenderOption } from './types'
import { resolveGenderSwitchFromMock } from './utils'
import { GenderSwitchButton } from './GenderSwitchButton'

const GENDER_LABEL: Record<GenderOption, string> = {
  men: 'Masculino',
  women: 'Feminino',
}

const getButtonClassName = (isSelected: boolean) => {
  if (isSelected) {
    return 'bg-restructure-background-gender-switch-active border-2 border-restructure-border-gender-switch-active text-restructure-gender-switch-active cursor-default'
  }

  return 'bg-transparent border border-transparent text-restructure-gender-switch-inactive hover:text-restructure-primary cursor-pointer'
}

export function GenderSwitch() {
  const isEnabled = Statsig.useStatsigFeatureGateValue(
    FEM_MASC_TENIS_PDP_EXPERIMENT_ID
  )

  const { pageData } = useGlobalContext()
  const { product, currentSku } = useProductPageContext()

  if (!isEnabled) {
    return null
  }

  const currentProductId = currentSku?.productId ?? product?.productId
  const matchedProduct = resolveGenderSwitchFromMock(currentProductId)

  if (!matchedProduct) {
    return null
  }

  const handleClickGender = (
    targetGender: GenderOption,
    targetUrl: string | null
  ) => {
    if (!targetUrl) {
      return
    }

    const standardObject = getStandardObject(pageData)
    const productObject = getProductQueryObject(currentSku)

    if (standardObject && productObject) {
      dispatchAmplitudeEvent({
        eventName: 'Product Gender Selected',
        eventData: {
          ...standardObject,
          'product details': [productObject],
          'old gender selected': GENDER_LABEL[matchedProduct.selectedGender],
          'new gender selected': GENDER_LABEL[targetGender],
        },
      })
    }

    navigate(targetUrl)
  }

  const isMenSelected = matchedProduct.selectedGender === 'men'
  const isWomenSelected = matchedProduct.selectedGender === 'women'

  const handleClickMen = () => {
    handleClickGender('men', matchedProduct.male.productUrl)
  }

  const handleClickWomen = () => {
    handleClickGender('women', matchedProduct.female.productUrl)
  }

  return (
    <div
      className="w-full max-w-[307px] bg-restructure-background-gender-switch-inactive rounded-full p-0 flex items-center"
      data-testid="gender-switch"
    >
      <GenderSwitchButton
        label="Masculino"
        isSelected={isMenSelected}
        onClick={handleClickMen}
        getButtonClassName={getButtonClassName}
        testId="gender-switch-men"
      />

      <GenderSwitchButton
        label="Feminino"
        isSelected={isWomenSelected}
        onClick={handleClickWomen}
        getButtonClassName={getButtonClassName}
        testId="gender-switch-women"
      />
    </div>
  )
}
